import { useEffect, useState } from 'react';
import { adminApi, adminAuth, publicApi } from '../lib/api';
import { toast, Toaster } from 'sonner';
import { Lock, LogOut, Plus, Trash2, Music2, ListMusic, Search as SearchIcon, ArrowUp, ArrowDown, Download, Star } from 'lucide-react';

const CREDS_KEY = 'pkp_admin_creds';

const Admin = () => {
  const [creds, setCreds] = useState(() => {
    try {
      const raw = localStorage.getItem(CREDS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  if (!creds) return <LoginForm onLogin={(c) => {
    localStorage.setItem(CREDS_KEY, JSON.stringify(c));
    setCreds(c);
  }} />;

  return <AdminDashboard creds={creds} onLogout={() => {
    localStorage.removeItem(CREDS_KEY);
    setCreds(null);
  }} />;
};

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAuth.login(username, password);
      onLogin({ username, password });
      toast.success('Welcome, admin!');
    } catch (err) {
      toast.error('Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="admin-login">
      <Toaster theme="dark" richColors />
      <form onSubmit={submit} className="w-full max-w-sm space-y-5">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FF007F] flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(255,0,127,0.4)]">
            <Lock className="w-6 h-6 text-[#120505]" />
          </div>
          <h1 className="font-display text-3xl text-[#FFF3E0] mt-4">Admin Studio</h1>
          <p className="text-xs text-[#BFA5A5] mt-1">Curate the beats.</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#BFA5A5]">Username</label>
          <input
            data-testid="admin-username-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            className="w-full mt-1 h-12 px-4 rounded-xl bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800] font-body"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#BFA5A5]">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full mt-1 h-12 px-4 rounded-xl bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800] font-body"
          />
        </div>
        <button
          data-testid="admin-login-btn"
          disabled={loading}
          className="w-full h-12 rounded-full bg-[#FFB800] text-[#120505] font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};

const AdminDashboard = ({ creds, onLogout }) => {
  const [tab, setTab] = useState('songs');
  const client = adminApi(creds.username, creds.password);

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <Toaster theme="dark" richColors />
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#120505]/85 border-b border-[#3D1A1A]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="font-display text-lg text-[#FFF3E0]">Admin Studio</div>
          <div className="flex-1" />
          <button
            data-testid="tab-songs-btn"
            onClick={() => setTab('songs')}
            className={`px-3 py-1.5 rounded-full text-sm ${tab === 'songs' ? 'bg-[#FFB800] text-[#120505]' : 'text-[#BFA5A5] hover:text-[#FFF3E0]'}`}
          >
            <Music2 className="w-4 h-4 inline mr-1" /> Songs
          </button>
          <button
            data-testid="tab-playlists-btn"
            onClick={() => setTab('playlists')}
            className={`px-3 py-1.5 rounded-full text-sm ${tab === 'playlists' ? 'bg-[#FFB800] text-[#120505]' : 'text-[#BFA5A5] hover:text-[#FFF3E0]'}`}
          >
            <ListMusic className="w-4 h-4 inline mr-1" /> Playlists
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="w-10 h-10 rounded-full flex items-center justify-center text-[#BFA5A5] hover:text-[#FF007F] hover:bg-[#220A0A]"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        {tab === 'songs' ? <SongsPanel client={client} /> : <PlaylistsPanel client={client} />}
      </main>
    </div>
  );
};

const SongsPanel = ({ client }) => {
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [urlInput, setUrlInput] = useState('');
  const [form, setForm] = useState({ youtube_video_id: '', title: '', artist: '', thumbnail_url: '', has_video: true, playlist_ids: [] });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [s, p] = await Promise.all([
      client.get('/admin/songs').then((r) => r.data),
      publicApi.listPlaylists(),
    ]);
    setSongs(s);
    setPlaylists(p);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const doFetch = async () => {
    if (!urlInput.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post('/admin/oembed', { url_or_id: urlInput.trim() });
      setForm((f) => ({
        ...f,
        youtube_video_id: data.youtube_video_id,
        title: data.title || f.title,
        artist: data.artist || f.artist,
        thumbnail_url: data.thumbnail_url || f.thumbnail_url,
      }));
      if (data.found) toast.success('Metadata fetched from YouTube.');
      else toast.info('oEmbed unavailable — please fill title/artist manually.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to fetch metadata');
    } finally { setBusy(false); }
  };

  const saveSong = async () => {
    if (!form.youtube_video_id || !form.title || !form.artist) {
      toast.error('YouTube ID, title & artist required.');
      return;
    }
    setBusy(true);
    try {
      await client.post('/admin/songs', form);
      toast.success('Song added!');
      setForm({ youtube_video_id: '', title: '', artist: '', thumbnail_url: '', has_video: true, playlist_ids: [] });
      setUrlInput('');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this song? It will be removed from all playlists.')) return;
    await client.delete(`/admin/songs/${id}`);
    toast.success('Deleted.');
    await refresh();
  };

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-[#3D1A1A] p-4 sm:p-6" data-testid="add-song-form">
        <h2 className="font-display text-2xl text-[#FFF3E0] mb-4">Add a Song</h2>

        <div className="flex gap-2 mb-4">
          <input
            data-testid="song-url-input"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
            className="flex-1 h-11 px-4 rounded-full bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800]"
          />
          <button
            data-testid="fetch-oembed-btn"
            onClick={doFetch}
            disabled={busy}
            className="h-11 px-5 rounded-full bg-[#FF007F] text-[#FFF3E0] font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            <SearchIcon className="w-4 h-4 inline mr-1" /> Fetch
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="YouTube Video ID" value={form.youtube_video_id} onChange={(v) => setForm((f) => ({ ...f, youtube_video_id: v }))} testId="form-yid" />
          <Field label="Thumbnail URL" value={form.thumbnail_url} onChange={(v) => setForm((f) => ({ ...f, thumbnail_url: v }))} testId="form-thumb" />
          <Field label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} testId="form-title" />
          <Field label="Artist" value={form.artist} onChange={(v) => setForm((f) => ({ ...f, artist: v }))} testId="form-artist" />
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-[#BFA5A5] mb-2">Add to playlists</div>
          <div className="flex flex-wrap gap-2">
            {playlists.map((p) => {
              const on = form.playlist_ids.includes(p.id);
              return (
                <button
                  data-testid={`toggle-playlist-${p.slug}`}
                  key={p.id}
                  onClick={() => setForm((f) => ({
                    ...f,
                    playlist_ids: on ? f.playlist_ids.filter((x) => x !== p.id) : [...f.playlist_ids, p.id],
                  }))}
                  className={`px-3 py-1.5 rounded-full text-xs border ${on ? 'bg-[#FFB800] border-[#FFB800] text-[#120505]' : 'border-[#3D1A1A] text-[#BFA5A5] hover:text-[#FFF3E0]'}`}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>

        <button
          data-testid="save-song-btn"
          onClick={saveSong}
          disabled={busy}
          className="mt-5 h-12 px-6 rounded-full bg-[#FFB800] text-[#120505] font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <Plus className="w-4 h-4 inline mr-1" /> Save Song
        </button>
      </section>

      <section>
        <h2 className="font-display text-2xl text-[#FFF3E0] mb-4">All Songs ({songs.length})</h2>
        <ul data-testid="admin-song-list" className="space-y-2">
          {songs.map((s) => (
            <li key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#3D1A1A]">
              <img src={s.thumbnail_url} alt="" className="w-12 h-12 rounded-md object-cover" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm text-[#FFF3E0]">{s.title}</div>
                <div className="text-xs text-[#BFA5A5] truncate">{s.artist} • {s.youtube_video_id}</div>
              </div>
              <button
                data-testid={`delete-song-${s.id}`}
                onClick={() => del(s.id)}
                className="w-10 h-10 rounded-full flex items-center justify-center text-[#FF007F] hover:bg-[#220A0A]"
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-[#BFA5A5]">{label}</span>
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full mt-1 h-11 px-3 rounded-lg bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800]"
    />
  </label>
);

const ImportPlaylistSection = ({ client, onDone, playlists }) => {
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [targetId, setTargetId] = useState('');
  const [newMeta, setNewMeta] = useState({ name: '', category_type: 'mood', tags: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = async () => {
    if (!url.trim()) { toast.error('Paste a YouTube playlist URL'); return; }
    if (mode === 'existing' && !targetId) { toast.error('Choose a target playlist'); return; }
    if (mode === 'new' && !newMeta.name.trim()) { toast.error('Give the new playlist a name'); return; }
    setBusy(true);
    setResult(null);
    try {
      const body = { playlist_url_or_id: url.trim() };
      if (mode === 'existing') body.target_playlist_id = targetId;
      else body.new_playlist = {
        name: newMeta.name.trim(),
        category_type: newMeta.category_type,
        tags: newMeta.tags.split(',').map((t) => t.trim()).filter(Boolean),
        description: newMeta.description,
      };
      const { data } = await client.post('/admin/import-playlist', body);
      setResult(data);
      toast.success(`Imported ${data.total_videos} videos · ${data.songs_added} new`);
      onDone && onDone();
      // Reset URL but keep meta
      setUrl('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="import-playlist-section"
      className="rounded-2xl border border-[#FF007F]/40 p-4 sm:p-6 bg-gradient-to-br from-[#FF007F]/8 to-transparent"
    >
      <div className="flex items-center gap-2 mb-1">
        <Download className="w-5 h-5 text-[#FF007F]" />
        <h2 className="font-display text-2xl text-[#FFF3E0]">Import from YouTube</h2>
      </div>
      <p className="text-xs text-[#BFA5A5] mb-4 font-body">
        Paste any public YouTube playlist URL — we'll pull every song with real titles, thumbnails and durations.
      </p>

      <input
        data-testid="import-url-input"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://www.youtube.com/playlist?list=PL..."
        className="w-full h-11 px-4 rounded-full bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FF007F]"
      />

      <div className="mt-4 flex gap-2">
        <button
          data-testid="import-mode-new"
          onClick={() => setMode('new')}
          className={`px-3 py-1.5 rounded-full text-xs ${mode === 'new' ? 'bg-[#FFB800] text-[#120505]' : 'border border-[#3D1A1A] text-[#BFA5A5]'}`}
        >
          Create new playlist
        </button>
        <button
          data-testid="import-mode-existing"
          onClick={() => setMode('existing')}
          className={`px-3 py-1.5 rounded-full text-xs ${mode === 'existing' ? 'bg-[#FFB800] text-[#120505]' : 'border border-[#3D1A1A] text-[#BFA5A5]'}`}
        >
          Add to existing
        </button>
      </div>

      {mode === 'new' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <Field label="Playlist name" value={newMeta.name} onChange={(v) => setNewMeta({ ...newMeta, name: v })} testId="import-new-name" />
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#BFA5A5]">Category</span>
            <select
              data-testid="import-new-category"
              value={newMeta.category_type}
              onChange={(e) => setNewMeta({ ...newMeta, category_type: e.target.value })}
              className="w-full mt-1 h-11 px-3 rounded-lg bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800]"
            >
              <option value="era">Era</option>
              <option value="mood">Mood</option>
            </select>
          </label>
          <Field label="Tags (comma-sep)" value={newMeta.tags} onChange={(v) => setNewMeta({ ...newMeta, tags: v })} testId="import-new-tags" />
          <Field label="Description" value={newMeta.description} onChange={(v) => setNewMeta({ ...newMeta, description: v })} testId="import-new-desc" />
        </div>
      ) : (
        <div className="mt-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#BFA5A5]">Add to</span>
            <select
              data-testid="import-target-select"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full mt-1 h-11 px-3 rounded-lg bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800]"
            >
              <option value="">— Choose a playlist —</option>
              {playlists.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <button
        data-testid="import-run-btn"
        onClick={doImport}
        disabled={busy}
        className="mt-5 h-12 px-6 rounded-full bg-[#FF007F] text-[#FFF3E0] font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 shadow-[0_6px_24px_rgba(255,0,127,0.35)]"
      >
        <Download className="w-4 h-4 inline mr-1" /> {busy ? 'Importing...' : 'Import Playlist'}
      </button>

      {result && (
        <div data-testid="import-result" className="mt-4 p-3 rounded-lg bg-[#220A0A] border border-[#3D1A1A] text-xs text-[#FFF3E0] font-body">
          Imported {result.total_videos} videos · {result.songs_added} new songs added · {result.songs_linked} linked to playlist
          {result.created_new_playlist && ' · New playlist created ✔'}
        </div>
      )}
    </section>
  );
};

const PlaylistsPanel = ({ client }) => {
  const [playlists, setPlaylists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [form, setForm] = useState({ name: '', slug: '', category_type: 'mood', tags: '', cover_image: '', description: '' });
  const [selected, setSelected] = useState(null); // playlist with songs
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [pls, ss] = await Promise.all([
      publicApi.listPlaylists(),
      client.get('/admin/songs').then((r) => r.data),
    ]);
    setPlaylists(pls);
    setSongs(ss);
    if (selected) {
      const fresh = pls.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.name || !form.category_type) return;
    setBusy(true);
    try {
      await client.post('/admin/playlists', {
        name: form.name,
        slug: form.slug || undefined,
        category_type: form.category_type,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        cover_image: form.cover_image,
        description: form.description,
      });
      toast.success('Playlist created.');
      setForm({ name: '', slug: '', category_type: 'mood', tags: '', cover_image: '', description: '' });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this playlist?')) return;
    await client.delete(`/admin/playlists/${id}`);
    toast.success('Deleted.');
    if (selected?.id === id) setSelected(null);
    await refresh();
  };

  const toggleFeatured = async (p) => {
    await client.patch(`/admin/playlists/${p.id}`, { featured: !p.featured });
    toast.success(p.featured ? 'Removed from featured' : 'Added to featured');
    await refresh();
  };

  const addSong = async (song_id) => {
    if (!selected) return;
    await client.post(`/admin/playlists/${selected.id}/add-song`, { song_id });
    toast.success('Added.');
    await refresh();
  };

  const removeSong = async (song_id) => {
    if (!selected) return;
    await client.post(`/admin/playlists/${selected.id}/remove-song`, { song_id });
    toast.success('Removed.');
    await refresh();
  };

  const reorder = async (idx, delta) => {
    if (!selected) return;
    const ids = [...selected.song_ids];
    const ni = idx + delta;
    if (ni < 0 || ni >= ids.length) return;
    [ids[idx], ids[ni]] = [ids[ni], ids[idx]];
    await client.post(`/admin/playlists/${selected.id}/reorder`, { song_ids: ids });
    await refresh();
  };

  const availableSongs = songs.filter((s) => !selected?.song_ids.includes(s.id));
  const playlistSongs = selected ? selected.song_ids.map((id) => songs.find((s) => s.id === id)).filter(Boolean) : [];

  return (
    <div className="space-y-8">
      <ImportPlaylistSection client={client} onDone={refresh} playlists={playlists} />

      <section className="rounded-2xl border border-[#3D1A1A] p-4 sm:p-6" data-testid="new-playlist-form">
        <h2 className="font-display text-2xl text-[#FFF3E0] mb-4">Create Playlist</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="pl-name" />
          <Field label="Slug (optional)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="pl-slug" />
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#BFA5A5]">Category</span>
            <select
              data-testid="pl-category"
              value={form.category_type}
              onChange={(e) => setForm({ ...form, category_type: e.target.value })}
              className="w-full mt-1 h-11 px-3 rounded-lg bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] focus:outline-none focus:border-[#FFB800]"
            >
              <option value="era">Era</option>
              <option value="mood">Mood</option>
            </select>
          </label>
          <Field label="Tags (comma-sep)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testId="pl-tags" />
          <Field label="Cover image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="pl-cover" />
          <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="pl-desc" />
        </div>
        <button
          data-testid="create-playlist-btn"
          onClick={create}
          disabled={busy}
          className="mt-4 h-11 px-5 rounded-full bg-[#FFB800] text-[#120505] font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <Plus className="w-4 h-4 inline mr-1" /> Create
        </button>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <h2 className="font-display text-2xl text-[#FFF3E0] mb-4">Playlists</h2>
          <ul className="space-y-2">
            {playlists.map((p) => (
              <li key={p.id}>
                <div
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selected?.id === p.id ? 'border-[#FFB800] bg-[#220A0A]' : 'border-[#3D1A1A]'}`}
                  onClick={() => setSelected(p)}
                  data-testid={`admin-playlist-${p.slug}`}
                >
                  <img src={p.cover_image} alt="" className="w-12 h-12 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#FFF3E0] truncate flex items-center gap-1.5">
                      {p.name}
                      {p.featured && <Star className="w-3 h-3 text-[#FFB800] fill-current shrink-0" />}
                    </div>
                    <div className="text-xs text-[#BFA5A5]">{p.category_type} · {p.song_ids.length} songs</div>
                  </div>
                  <button
                    data-testid={`toggle-featured-${p.slug}`}
                    onClick={(e) => { e.stopPropagation(); toggleFeatured(p); }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#3D1A1A] ${p.featured ? 'text-[#FFB800]' : 'text-[#BFA5A5]'}`}
                    aria-label={p.featured ? 'Unfeature' : 'Feature'}
                    title={p.featured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    <Star className={`w-4 h-4 ${p.featured ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    data-testid={`delete-playlist-${p.slug}`}
                    onClick={(e) => { e.stopPropagation(); del(p.id); }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-[#FF007F] hover:bg-[#3D1A1A]"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl text-[#FFF3E0] mb-4">
            {selected ? `Manage: ${selected.name}` : 'Select a playlist'}
          </h2>
          {selected && (
            <>
              <div className="mb-3 text-xs text-[#BFA5A5] uppercase tracking-wider">In this playlist ({playlistSongs.length})</div>
              <ul className="space-y-2 mb-6" data-testid="playlist-songs">
                {playlistSongs.map((s, i) => (
                  <li key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#220A0A] border border-[#3D1A1A]">
                    <div className="w-6 text-center text-xs text-[#BFA5A5]">{i + 1}</div>
                    <img src={s.thumbnail_url} alt="" className="w-10 h-10 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#FFF3E0] truncate">{s.title}</div>
                      <div className="text-xs text-[#BFA5A5] truncate">{s.artist}</div>
                    </div>
                    <button data-testid={`move-up-${i}`} onClick={() => reorder(i, -1)} className="w-8 h-8 rounded-full hover:bg-[#3D1A1A] flex items-center justify-center"><ArrowUp className="w-4 h-4" /></button>
                    <button data-testid={`move-down-${i}`} onClick={() => reorder(i, 1)} className="w-8 h-8 rounded-full hover:bg-[#3D1A1A] flex items-center justify-center"><ArrowDown className="w-4 h-4" /></button>
                    <button data-testid={`remove-song-${i}`} onClick={() => removeSong(s.id)} className="w-8 h-8 rounded-full hover:bg-[#3D1A1A] flex items-center justify-center text-[#FF007F]"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-[#BFA5A5] uppercase tracking-wider mb-2">Available songs</div>
              <ul className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {availableSongs.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#3D1A1A]">
                    <img src={s.thumbnail_url} alt="" className="w-10 h-10 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#FFF3E0] truncate">{s.title}</div>
                      <div className="text-xs text-[#BFA5A5] truncate">{s.artist}</div>
                    </div>
                    <button
                      data-testid={`add-to-playlist-${s.id}`}
                      onClick={() => addSong(s.id)}
                      className="w-8 h-8 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                      aria-label="Add"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Admin;
